import { api } from "./api";

/**
 * Web Push opt-in (spec §74/§75): registrace service workeru + subscription.
 * Volá se jen z explicitní uživatelské akce (žádné otravné auto-prompty).
 */

function base64ToUint8(base64: string): Uint8Array {
  const padding = "=".repeat((4 - (base64.length % 4)) % 4);
  const raw = atob((base64 + padding).replace(/-/g, "+").replace(/_/g, "/"));
  return Uint8Array.from(raw, (c) => c.charCodeAt(0));
}

export function pushSupported(): boolean {
  return "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
}

export async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!("serviceWorker" in navigator)) return null;
  try {
    return await navigator.serviceWorker.register("/sw.js");
  } catch {
    return null;
  }
}

export type PushState = "unsupported" | "unavailable" | "denied" | "subscribed" | "ready";

/** Zjistí stav bez vyvolání promptu. */
export async function getPushState(): Promise<PushState> {
  if (!pushSupported()) return "unsupported";
  try {
    await api.pushPublicKey();
  } catch {
    return "unavailable"; // server nemá VAPID klíče
  }
  if (Notification.permission === "denied") return "denied";
  const registration = await navigator.serviceWorker.getRegistration();
  const existing = await registration?.pushManager.getSubscription();
  return existing ? "subscribed" : "ready";
}

/** Vyžádá permission a založí subscription (idempotentní). */
export async function enablePush(): Promise<boolean> {
  if (!pushSupported()) return false;
  const { publicKey } = await api.pushPublicKey();
  const permission = await Notification.requestPermission();
  if (permission !== "granted") return false;
  const registration = (await navigator.serviceWorker.getRegistration()) ?? (await registerServiceWorker());
  if (!registration) return false;
  const subscription =
    (await registration.pushManager.getSubscription()) ??
    (await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: base64ToUint8(publicKey).buffer as ArrayBuffer,
    }));
  const json = subscription.toJSON();
  if (!json.endpoint || !json.keys?.p256dh || !json.keys.auth) return false;
  await api.pushSubscribe({
    endpoint: json.endpoint,
    keys: { p256dh: json.keys.p256dh, auth: json.keys.auth },
  });
  return true;
}
